import React, { useState } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';

const QCMConceptualMapping = () => {
    const { t } = useTranslation();
    const {
        currentQCM,
        submitConceptualMapping,
        conceptualMappingAnswers
    } = useQCM();

    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverTarget, setDragOverTarget] = useState(null);

    if (!currentQCM?.conceptual_Mapping) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-400">No conceptual mapping available</p>
            </div>
        );
    }

    const handleDragStart = (e, solution) => {
        setDraggedItem(solution);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, problem) => {
        e.preventDefault();
        setDragOverTarget(problem);
    };

    const handleDrop = async (e, problem) => {
        e.preventDefault();
        if (draggedItem && problem) {
            await submitConceptualMapping(problem.conceptual_mapping_id, draggedItem.conceptual_mapping_solution);
        }
        setDraggedItem(null);
        setDragOverTarget(null);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDragOverTarget(null);
    };

    const problems = currentQCM.conceptual_Mapping.map(cm => cm.conceptual_mapping_problem);
    const solutions = currentQCM.conceptual_Mapping.map(cm => cm.conceptual_mapping_solution);
    
    // Randomize solutions order
    const shuffledSolutions = [...solutions].sort(() => Math.random() - 0.5);

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-blue-400 mb-2 flex items-center justify-center">
                    <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7c0-2.21-3.582-4-8-4s-8 1.79-8 4z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3c-4.418 0-8 1.79-8 4s3.582 4 8 4 8-1.79 8-4-3.582-4-8-4z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c-2.209 0-4-1.791-4-4s1.791-4 4-4 4 1.791 4 4-1.791 4-4 4z" />
                    </svg>
                    Map the solution
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                    Associate each conceptual problem with its appropriate solution using drag and drop.
                </p>
                
                {(() => {
                    const totalQuestions = currentQCM.conceptual_Mapping.length;
                    
                    // Debug: Afficher le contenu de conceptualMappingAnswers
                    console.log('🔍 conceptualMappingAnswers:', conceptualMappingAnswers);
                    console.log('🔍 totalQuestions:', totalQuestions);
                    
                    // Obtenir les IDs des questions valides
                    const validQuestionIds = currentQCM.conceptual_Mapping.map(mapping => mapping.conceptual_mapping_id);
                    
                    // Filtrer seulement les réponses qui correspondent aux questions valides
                    const validAnswers = Object.entries(conceptualMappingAnswers)
                        .filter(([mappingId, answer]) => 
                            validQuestionIds.includes(mappingId) &&
                            answer && 
                            answer.answer && 
                            answer.answer.trim() !== '' &&
                            typeof answer.isCorrect === 'boolean'
                        )
                        .map(([mappingId, answer]) => answer);
                    
                    const totalAnswers = validAnswers.length;
                    const correctAnswers = validAnswers.filter(answer => answer.isCorrect).length;
                    const incorrectAnswers = totalAnswers - correctAnswers;
                    const remainingQuestions = totalQuestions - totalAnswers;
                    
                    console.log('🔍 validAnswers:', validAnswers);
                    console.log('🔍 totalAnswers:', totalAnswers);
                    console.log('🔍 correctAnswers:', correctAnswers);
                    
                    const successRate = Math.round((correctAnswers / totalQuestions) * 100);
                    
                    // Calcul des pourcentages pour chaque section de la barre
                    const correctWidth = (correctAnswers / totalQuestions) * 100;
                    const incorrectWidth = (incorrectAnswers / totalQuestions) * 100;
                    const remainingWidth = (remainingQuestions / totalQuestions) * 100;
                    
                    return (
                        <>
                            {/* Progress Bar - 3 sections */}
                            <div className="w-full bg-gray-300 rounded-full h-3 mb-2 flex overflow-hidden">
                                {/* Section verte - Correct */}
                                <div 
                                    className="bg-green-500 h-full transition-all duration-300"
                                    style={{ width: `${correctWidth}%` }}
                                ></div>
                                {/* Section rouge - Incorrect */}
                                <div 
                                    className="bg-red-500 h-full transition-all duration-300"
                                    style={{ width: `${incorrectWidth}%` }}
                                ></div>
                                {/* Section grise - À faire */}
                                <div 
                                    className="bg-gray-500 h-full transition-all duration-300"
                                    style={{ width: `${remainingWidth}%` }}
                                ></div>
                            </div>
                            <p className="text-sm text-gray-300">
                                <span className="font-bold text-blue-400">{correctAnswers} / {totalQuestions}</span> completed with success 
                                <span className="ml-2 text-blue-400 font-bold">({successRate}%)</span>
                            </p>
                        </>
                    );
                })()}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Problèmes */}
                <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-red-400 mb-3">
                        Problems
                    </h4>
                    {problems.map((problem, index) => {
                        // Find if this problem has been matched with a solution
                        const originalMapping = currentQCM.conceptual_Mapping[index];
                        const isAnswered = conceptualMappingAnswers[originalMapping.conceptual_mapping_id];
                        const isCorrect = isAnswered?.isCorrect;
                        
                        return (
                            <div
                                key={`problem-${index}`}
                                onDragOver={(e) => handleDragOver(e, originalMapping)}
                                onDrop={(e) => handleDrop(e, originalMapping)}
                                className={`p-4 border-2 rounded-lg transition-all duration-200 ${
                                    isCorrect
                                        ? 'bg-green-900/20 border-green-500'
                                        : isAnswered
                                        ? 'bg-red-900/20 border-red-500'
                                        : dragOverTarget?.conceptual_mapping_id === originalMapping.conceptual_mapping_id
                                        ? 'bg-blue-900/20 border-blue-500'
                                        : 'bg-red-900/20 border-red-500/30'
                                }`}
                            >
                                <p className="text-gray-200">{problem}</p>
                                {/* Show extract of dropped solution if answered */}
                                {isAnswered && (
                                    <div className="mt-2 pt-2 border-t border-gray-600">
                                        <p className="text-gray-400">
                                            Matched with: {isAnswered.answer?.slice(0, 50)}...
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Solutions */}
                <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-green-400 mb-3">
                        Solutions
                    </h4>
                    {shuffledSolutions.map((solution, index) => {
                        // Find the original mapping for this solution
                        const originalMapping = currentQCM.conceptual_Mapping.find(cm => cm.conceptual_mapping_solution === solution);
                        const isCorrect = conceptualMappingAnswers[originalMapping.conceptual_mapping_id]?.isCorrect;
                        const isAnswered = conceptualMappingAnswers[originalMapping.conceptual_mapping_id];

                        return (
                            <div
                                key={`solution-${index}`}
                                draggable={!isAnswered}
                                onDragStart={(e) => handleDragStart(e, originalMapping)}
                                onDragEnd={handleDragEnd}
                                className={`p-4 border-2 rounded-lg transition-all duration-200 ${
                                    isAnswered
                                        ? 'bg-gray-600 border-gray-500 opacity-50 cursor-not-allowed'
                                        : draggedItem?.conceptual_mapping_id === originalMapping.conceptual_mapping_id
                                        ? 'opacity-50 cursor-move'
                                        : 'bg-green-900/20 border-green-500/30 cursor-move hover:bg-green-900/30'
                                }`}
                            >
                                <p className="text-gray-200">{solution}</p>
                                {isAnswered && (
                                    <div className="mt-2 text-xs text-gray-400">
                                        Already used
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Instructions */}
            <div className="mt-6 p-4 bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-400">
                    Associate each conceptual problem with its appropriate solution using drag and drop.
                </p>
            </div>
        </div>
    );
};

export default QCMConceptualMapping; 